import { ChipMarker } from "@components/frame-editor/chip-markers";
import { DrawingPath } from "@components/frame-editor/drawing-canvas";
import { TransformType } from "@components/frame-editor/pan-size-component";
import {
  AdjustmentSettings,
  defaultSettings,
} from "@views/app/studio/components/actions/effects/cursors";
import _ from "lodash";
import { createContext, useCallback, useContext } from "react";

// Type for each chip's edit data
// Note: attachment is always stored as a string (base64 data URL or URL) for persistence
export type ChipEditData = {
  chipId: string;
  prompt: string;
  attachment: string | null; // Always base64 data URL or URL string (File objects are converted immediately)
  loraId: string;
  mode: "none" | "image" | "model"; // "none" means attachment section is collapsed
};

export type StudioStateType = {
  tool?: string;
  edit_tool?: string; // Specific tool when in edit mode (face, object, etc)
  is_editing: boolean;
  effects: { filter: string | null; adjustements: Partial<AdjustmentSettings> };
  frame: { width: number; height: number };
  drawing?: DrawingPath[];
  drawing_svg_ref?: React.RefObject<SVGSVGElement>;
  transform: TransformType;
  ts: number; // Timestamp of the last change
  // Chip markers for point-and-click editing
  chips?: ChipMarker[];
  chipEdits?: ChipEditData[];
  selectedChipId?: string | null;
};

export type StudioRedoableStateType = Pick<
  StudioStateType,
  "transform" | "effects" | "frame" | "drawing"
>;

export const emptyStudioState: StudioStateType = {
  is_editing: false,
  effects: { filter: null, adjustements: defaultSettings },
  frame: { width: 1080, height: 1080 },
  transform: {
    scaleX: 1,
    scaleY: 1,
    rotation: 0,
    x: 0,
    y: 0,
    changed: false,
  },
  ts: 0,
  chips: [],
  chipEdits: [],
  selectedChipId: null,
};

export const StudioContext = createContext<{
  studio: StudioStateType;
  setStudio: (newState: Partial<StudioStateType>) => void;
  undos: {
    redos: StudioRedoableStateType[];
    undos: StudioRedoableStateType[];
  };
  setUndos: (newState: {
    redos: StudioRedoableStateType[];
    undos: StudioRedoableStateType[];
  }) => void;
}>({
  studio: emptyStudioState,
  setStudio: () => {},
  undos: { redos: [], undos: [] },
  setUndos: () => {},
});

let lastChangesTs = Date.now();

export const useStudio = () => {
  const context = useContext(StudioContext);

  const setStudio = (newState: Partial<StudioStateType>) => {
    const { studio, setStudio: _setStudio, undos, setUndos } = context;

    console.log("Setting studio state:", newState, "from state", studio.tool);

    let hasChanges = !_.isEqual(
      JSON.parse(
        JSON.stringify(
          _.pick(studio, "transform", "effects", "frame", "drawing")
        )
      ),
      JSON.parse(
        JSON.stringify(
          _.pick(
            { ...studio, ...newState },
            "transform",
            "effects",
            "frame",
            "drawing"
          )
        )
      )
    );

    const nextState = {
      ...newState,
      ts: Date.now(),
    };

    _setStudio(nextState);

    if (hasChanges && lastChangesTs < Date.now() - 1000 && studio.is_editing) {
      console.log("Changes detected, updating undos");

      setUndos({
        undos: [
          _.cloneDeep(
            _.pick(studio, "transform", "effects", "frame", "drawing")
          ),
          ...(undos.undos || []),
        ],
        redos: [],
      });
    }

    lastChangesTs = Date.now();
  };

  const undo = useCallback(() => {
    context.setStudio({
      ...context.studio,
      transform: context.undos.undos![0].transform,
      effects: context.undos.undos![0].effects,
      frame: context.undos.undos![0].frame,
      drawing: context.undos.undos![0].drawing,
    });
    context.setUndos({
      undos: context.undos.undos!.slice(1),
      redos: [
        _.cloneDeep(
          _.pick(context.studio, "transform", "effects", "frame", "drawing")
        ),
        ...(context.undos.redos || []),
      ],
    });
  }, [context]);

  const redo = useCallback(() => {
    context.setStudio({
      ...context.studio,
      transform: context.undos.redos![0].transform,
      effects: context.undos.redos![0].effects,
      frame: context.undos.redos![0].frame,
      drawing: context.undos.redos![0].drawing,
    });
    context.setUndos({
      redos: context.undos.redos!.slice(1),
      undos: [
        _.cloneDeep(
          _.pick(context.studio, "transform", "effects", "frame", "drawing")
        ),
        ...(context.undos.undos || []),
      ],
    });
  }, [context]);

  return {
    studio: context.studio,
    setStudio,
    undos: context.undos,
    undo,
    redo,
  };
};
